'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { LocaleSwitcherProps } from '@routeroyal/theme-core';

interface LocaleOption {
  code: string;
  name: string;
}

const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
];

export function RouteRoyalLocaleSwitcher({ locale, className, inMobileMenu = false }: LocaleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRectRef = useRef<DOMRect | null>(null);
  const [, forceUpdate] = useState(0);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();

  const currentLocaleData = SUPPORTED_LOCALES.find(l => l.code === locale) ?? SUPPORTED_LOCALES[0]!;
  const otherLocales = SUPPORTED_LOCALES.filter(l => l.code !== locale);

  const getLocalizedPath = useCallback((targetLocale: string) => {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0 && SUPPORTED_LOCALES.some(l => l.code === pathSegments[0])) {
      pathSegments[0] = targetLocale;
      return '/' + pathSegments.join('/');
    }
    return `/${targetLocale}${pathname === '/' ? '' : pathname}`;
  }, [pathname]);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current && !inMobileMenu) {
      buttonRectRef.current = buttonRef.current.getBoundingClientRect();
      forceUpdate(n => n + 1);
    }
  }, [isOpen, inMobileMenu]);

  useEffect(() => {
    if (!isOpen || !inMobileMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, inMobileMenu]);

  const dropdownContent = (
    <div className="py-1">
      {otherLocales.map((localeOption) => (
        <Link
          key={localeOption.code}
          href={getLocalizedPath(localeOption.code)}
          onClick={() => setIsOpen(false)}
          className="block w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors duration-150 min-h-[44px]"
        >
          <div className="flex items-center gap-3">
            <div>
              <span className="font-semibold text-gray-900 text-sm">{localeOption.code.toUpperCase()}</span>
              <span className="block text-xs text-gray-500">{localeOption.name}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className={`relative ${className || ''}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen && buttonRef.current && !inMobileMenu) {
            buttonRectRef.current = buttonRef.current.getBoundingClientRect();
          }
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center gap-1.5 rounded-lg bg-transparent border border-gray-200 text-gray-700 hover:text-blue-900 hover:border-blue-300 hover:bg-blue-50 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30 font-medium text-sm ${
          inMobileMenu ? 'px-4 py-3 min-h-[48px]' : 'px-3 py-1.5'
        }`}
        aria-label={`${currentLocaleData.code.toUpperCase()}: Select language`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
        <span>{currentLocaleData.code.toUpperCase()}</span>
        <svg className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {inMobileMenu && isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[100]" role="listbox">
          {dropdownContent}
        </div>
      )}

      {!inMobileMenu && mounted && createPortal(
        isOpen && buttonRectRef.current ? (
          <>
            <div
              style={{
                position: 'fixed',
                top: buttonRectRef.current.bottom + 6,
                right: window.innerWidth - buttonRectRef.current.right,
              }}
              className="min-w-[170px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-[10000]"
              role="listbox"
            >
              {dropdownContent}
            </div>
            <div className="fixed inset-0 z-[9999]" onClick={() => setIsOpen(false)} aria-hidden="true" />
          </>
        ) : null,
        document.body
      )}
    </div>
  );
}
